import type { ReelTranscriptCollapseView } from './useReelTranscriptCollapse';
import type {
  ReelTranscriptChapter,
  ReelTranscriptSection,
  ReelTranscriptStructure,
  ReelWordStamp,
} from '@src/services/reel/reelTranscriptStructure';

/**
 * PLAT-11157 — the pure half of drawing headings into the prose: split the
 * paragraph groups where a Section starts, then interleave the heading rows.
 * The renderer maps the result; nothing here touches React.
 */

/** The slice of a paragraph group this module reads and re-slices. */
type ParagraphGroupLike = {
  groupKey: string;
  startTime: number | undefined;
  speakerLabel?: string | undefined;
  isNewSpeaker?: boolean;
  elements: Array<{
    element: {
      type?: string;
      state?: string;
      value?: string | null;
      ts?: number | null;
    };
    index: number;
  }>;
};

/**
 * A slice of a split group. `reelSpeakerScopeIndices` carries the ORIGINAL
 * paragraph's element indices: the speaker header renders on the first slice
 * only, and a speaker action from it must cover the whole paragraph, not the
 * half the boundary left it with.
 */
export type ReelSplitGroup<TGroup> = TGroup & {
  reelSpeakerScopeIndices?: number[];
};

export type ReelTranscriptRow<TGroup> =
  | {
      kind: 'chapterHeading';
      chapter: ReelTranscriptChapter;
      collapsed: boolean;
    }
  | {
      kind: 'sectionHeading';
      section: ReelTranscriptSection;
      collapsed: boolean;
    }
  /**
   * The gap a fold leaves behind — a Chapter's or a Section's: the boundary the
   * folded run ends at, holding the time the fold took off the page. Folding
   * must not close the seam between two clips — that seam is where the reel is
   * edited (inserting a clip there, and more to come), so it stays addressable
   * whether or not the words under it are showing.
   */
  | {
      kind: 'gap';
      /** Stable across renders: what folded, not where it landed. */
      id: string;
      /** Reel time the folded run ends at. */
      endTime: number;
    }
  | {
      kind: 'paragraph';
      group: ReelSplitGroup<TGroup> & { isNewSpeaker: boolean };
    };

/**
 * The seam a folded run leaves: where it ends. Over one Section or over a whole
 * Chapter's worth of them — the last one's end is the boundary either way,
 * which is the only difference between the two folds from this row's side.
 */
const gapRow = <TGroup>(
  id: string,
  sections: readonly ReelTranscriptSection[],
): Extract<ReelTranscriptRow<TGroup>, { kind: 'gap' }> => {
  const last = sections[sections.length - 1];
  return { kind: 'gap', id, endTime: last.startTime + last.duration };
};

/** The renderer's own visibility rule — a slice of only removed/whitespace
 * tokens must not become a paragraph, exactly as `visibleByContent` already
 * guarantees for whole groups upstream of the split. */
const hasRenderableContent = (group: ParagraphGroupLike) =>
  group.elements.some(
    ({ element }) =>
      element.state !== 'removed' &&
      element.state !== 'cut' &&
      typeof element.value === 'string' &&
      element.value.trim() !== '',
  );

/**
 * Splits any group that spans a Section boundary, so every group belongs to
 * one Section (or to none). A boundary can fall mid-paragraph — the reel cuts
 * where the creator cut, not where the paragraph ends — and a heading must
 * sit exactly at its passage's first word, so the spanning group is divided
 * rather than the heading snapped to the nearest paragraph edge.
 *
 * A slice with nothing renderable in it (only removed words around the cut)
 * is dropped rather than emitted — upstream filtering guaranteed that for
 * whole groups, and the split must not undo it.
 *
 * The suffix keeps the original's fields (same `paragraphId`, so the topic
 * rail still finds it) under a derived key, with `startTime` re-read from its
 * own first word so the same-speaker timestamp row stays truthful.
 */
export function splitGroupsAtReelSections<TGroup extends ParagraphGroupLike>(
  groups: readonly TGroup[],
  stamp: ReelWordStamp,
): ReelSplitGroup<TGroup>[] {
  const boundaryIndexes = new Set(stamp.firstWordIndexBySectionId.values());

  const result: ReelSplitGroup<TGroup>[] = [];
  for (const group of groups) {
    const slices: ReelSplitGroup<TGroup>[] = [];
    let sliceStart = 0;
    for (let i = 0; i < group.elements.length; i++) {
      if (i === sliceStart) continue;
      if (!boundaryIndexes.has(group.elements[i].index)) continue;
      slices.push(sliceGroup(group, sliceStart, i, stamp, sliceStart > 0));
      sliceStart = i;
    }
    if (sliceStart === 0) {
      result.push(group);
      continue;
    }
    slices.push(
      sliceGroup(group, sliceStart, group.elements.length, stamp, true),
    );
    result.push(...slices.filter(hasRenderableContent));
  }
  return result;
}

function sliceGroup<TGroup extends ParagraphGroupLike>(
  group: TGroup,
  from: number,
  to: number,
  stamp: ReelWordStamp,
  isSuffix: boolean,
): ReelSplitGroup<TGroup> {
  const elements = group.elements.slice(from, to);
  const reelSpeakerScopeIndices = group.elements.map(({ index }) => index);
  if (!isSuffix) return { ...group, elements, reelSpeakerScopeIndices };

  const firstWord = elements.find(
    ({ element }) => element.type === 'text' && typeof element.ts === 'number',
  );
  const owner = firstWord
    ? stamp.sectionIdByWordIndex.get(firstWord.index)
    : undefined;
  return {
    ...group,
    elements,
    reelSpeakerScopeIndices,
    groupKey: `${group.groupKey}__reel-${owner ?? from}`,
    startTime: firstWord ? (firstWord.element.ts ?? undefined) : undefined,
  };
}

/**
 * Interleaves the heading rows into the (already split, already filtered)
 * groups: a Chapter's heading before its first visible Section, then each
 * Section's heading before its first group. The reel's own summary is not a
 * row here — the host renders it under the transcript's toolbar. Everything
 * stays a flat sibling — never a wrapper — so text selection across a heading
 * keeps working, the same reason the speaker labels sit inline.
 *
 * Placement is "the first group this Section owns" rather than an index
 * comparison, so a heading survives the paragraph filters: when a topic
 * filter hides a Section's opening paragraph, its heading moves to the first
 * paragraph still showing instead of disappearing.
 *
 * A Chapter's heading appears only when something of it is on the page: its
 * own first visible Section, or — for a Chapter that owns no Sections at
 * all — its place in the order, matching the Reel Editor's list. A Chapter
 * whose passages are all filtered away emits nothing, so a "Show Only"
 * filter never stacks headings that advertise content the page doesn't have.
 */
export function buildReelTranscriptRows<TGroup extends ParagraphGroupLike>(
  groups: readonly ReelSplitGroup<TGroup>[],
  structure: ReelTranscriptStructure,
  stamp: ReelWordStamp,
  collapse?: Pick<
    ReelTranscriptCollapseView,
    'collapsedChapterIds' | 'collapsedSectionIds'
  >,
): ReelTranscriptRow<TGroup>[] {
  const sectionsById = new Map(
    structure.sections.map(section => [section.sectionId, section]),
  );
  const chapterPositionBySectionId = new Map<string, number>();
  structure.chapters.forEach((chapter, position) => {
    for (const section of chapter.sections) {
      chapterPositionBySectionId.set(section.sectionId, position);
    }
  });

  const isChapterCollapsed = (position: number | undefined) =>
    position !== undefined &&
    (collapse?.collapsedChapterIds.has(
      structure.chapters[position].chapterId,
    ) ??
      false);
  const isSectionHidden = (
    sectionId: string,
    chapterPosition: number | undefined,
  ) =>
    isChapterCollapsed(chapterPosition) ||
    (collapse?.collapsedSectionIds.has(sectionId) ?? false);

  const rows: ReelTranscriptRow<TGroup>[] = [];
  const emittedSectionIds = new Set<string>();
  let chaptersFlushed = 0;

  // Walks the chapter list up to `position`, emitting the target chapter and
  // any EMPTY chapters holding their place before it. A skipped chapter that
  // owns Sections is one whose passages the current filters hide — its
  // heading stays off the page with them.
  const flushChaptersThrough = (position: number, includeTarget: boolean) => {
    while (chaptersFlushed <= position) {
      const chapter = structure.chapters[chaptersFlushed];
      const isTarget = chaptersFlushed === position && includeTarget;
      if (isTarget || chapter.sections.length === 0) {
        const isCollapsed =
          collapse?.collapsedChapterIds.has(chapter.chapterId) ?? false;
        rows.push({ kind: 'chapterHeading', chapter, collapsed: isCollapsed });
        // A folded Chapter leaves the same seam a folded Section does, over the
        // whole run it holds. A Chapter with nothing in it has no boundary to
        // state, so it emits none.
        if (isCollapsed && chapter.sections.length > 0) {
          rows.push(gapRow(`chapter-${chapter.chapterId}`, chapter.sections));
        }
      }
      chaptersFlushed += 1;
    }
  };

  // Folding removes paragraphs, so the visible-sequence speaker headers are
  // recomputed over what actually survives — a paragraph whose same-speaker
  // predecessor folded away must reintroduce its speaker.
  let lastVisibleSpeaker: string | undefined;
  const pushParagraph = (group: ReelSplitGroup<TGroup>) => {
    const isNewSpeaker = group.speakerLabel !== lastVisibleSpeaker;
    lastVisibleSpeaker = group.speakerLabel;
    rows.push({
      kind: 'paragraph',
      group: { ...group, isNewSpeaker },
    });
  };

  for (const group of groups) {
    const firstStamped = group.elements.find(
      ({ index }) => stamp.sectionIdByWordIndex.get(index) !== undefined,
    );
    const sectionId = firstStamped
      ? stamp.sectionIdByWordIndex.get(firstStamped.index)
      : undefined;

    // Words the reel does not own always stay on the page — folding a reel
    // hides the reel's passages, not material that was never in one.
    if (sectionId === undefined) {
      pushParagraph(group);
      continue;
    }

    const chapterPosition = chapterPositionBySectionId.get(sectionId);
    if (!emittedSectionIds.has(sectionId)) {
      const section = sectionsById.get(sectionId);
      // Only a Section the structure knows gets marked emitted — when the
      // stamp and the structure momentarily disagree (they derive from the
      // same timeline in separate passes), the Section stays eligible for a
      // heading on the next group instead of silently losing it.
      if (section) {
        emittedSectionIds.add(sectionId);
        if (chapterPosition !== undefined) {
          flushChaptersThrough(chapterPosition, true);
        }
        // A Section inside a folded Chapter folds heading and all; its own
        // collapsed flag only matters while its Chapter is open.
        if (!isChapterCollapsed(chapterPosition)) {
          const isCollapsed =
            collapse?.collapsedSectionIds.has(sectionId) ?? false;
          rows.push({
            kind: 'sectionHeading',
            section,
            collapsed: isCollapsed,
          });
          // Inside a folded Chapter the whole run is away and the Chapter's
          // own seam already stands for it — a row per Section there would put
          // back most of what the fold was for.
          if (isCollapsed) {
            rows.push(gapRow(`section-${sectionId}`, [section]));
          }
        }
      }
    }

    if (!isSectionHidden(sectionId, chapterPosition)) {
      pushParagraph(group);
    }
  }

  // Trailing EMPTY Chapters keep their place at the end of the list; trailing
  // Chapters whose passages are filtered away emit nothing.
  flushChaptersThrough(structure.chapters.length - 1, false);

  return rows;
}
